/**
 * 破坏性颜色调整（DESIGN.md §6.5 补充：「图像 → 调整」，参照 Photoshop）。
 *
 * 跟"图层"菜单里的调整图层（`app/actions/adjustment-actions.ts`）是完全
 * 不同的两条路：调整图层非破坏性、参数存文档、合成时应用；这里是真实 PS
 * 的"图像→调整"语义——直接改**当前活动 raster 图层**的像素，一次调用
 * 一条历史，不新建任何图层对象。数学全部复用 `engine/adjustments/lut.ts`/
 * `basic.ts` 里已经跟 GPU 对拍过的同一份公式，两条路径视觉效果一致。
 *
 * 有选区时只影响选区内（按覆盖度线性插值原值/调整后值，跟填充/删除同一套
 * 调制约定）；没有选区时整个活动图层生效。透明像素（alpha=0）跳过——
 * 调色调的是"看得见的颜色"，不该把透明区域调出颜色来。
 */
import { TILE, tileKey, type LayerId } from '../engine/tile/key';
import { BYTES_PER_TILE } from '../engine/tile/store';
import { adjustHsl, curvesLut, levelsLut, type CurvePoint, type HslParams, type LevelsParams } from '../engine/adjustments/lut';
import { basicToneLut, DEFAULT_BASIC_PARAMS } from '../engine/adjustments/basic';
import type { AppStore } from './store';

/** 单像素颜色变换：输入输出都是 sRGB 0–255 整数。 */
export type PixelColorTransform = (
  r: number,
  g: number,
  b: number,
  a: number,
) => readonly [number, number, number, number];

/**
 * 通用落地通道：所有破坏性调整都走这一个函数，只是 `transform` 不同。
 * 返回是否真的发生了修改（无文档/无活动 raster 图层/选区为空时为 false）。
 */
export function applyDestructiveAdjustment(
  store: AppStore,
  label: string,
  transform: PixelColorTransform,
): boolean {
  const document = store.document;
  if (document === null) return false;
  const target: LayerId | null = store.activeRasterLayerId();
  if (target === null) return false;

  const rawSelection = store.selection;
  const selection = rawSelection !== null && !rawSelection.isEmpty ? rawSelection : null;
  const bounds = selection !== null ? selection.contentBounds() : { x: 0, y: 0, ...document.size };
  if (bounds === null || bounds.width <= 0 || bounds.height <= 0) return false;

  const transaction = store.beginPixelTransaction(label);
  const tx0 = Math.floor(bounds.x / TILE);
  const ty0 = Math.floor(bounds.y / TILE);
  const tx1 = Math.floor((bounds.x + bounds.width - 1) / TILE);
  const ty1 = Math.floor((bounds.y + bounds.height - 1) / TILE);

  const keys: Array<{ tx: number; ty: number; key: ReturnType<typeof tileKey> }> = [];
  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) {
      const key = tileKey(target, tx, ty);
      transaction.touch(key);
      keys.push({ tx, ty, key });
    }
  }
  transaction.capture();

  const touched: Array<{ tx: number; ty: number }> = [];
  const row = new Uint8Array(TILE);
  for (const { tx, ty, key } of keys) {
    const existing = store.tiles.get(key)?.data;
    if (existing == null) continue; // 没画过的块是透明的，调色调不出内容
    const out = new Uint8Array(BYTES_PER_TILE);
    out.set(existing);
    let changed = false;

    const top = Math.max(bounds.y, ty * TILE);
    const bottom = Math.min(bounds.y + bounds.height, (ty + 1) * TILE);
    const left = Math.max(bounds.x, tx * TILE);
    const right = Math.min(bounds.x + bounds.width, (tx + 1) * TILE);
    for (let y = top; y < bottom; y++) {
      if (selection !== null) selection.readRow(y, left, right, row);
      else row.fill(255, 0, right - left);
      for (let x = left; x < right; x++) {
        const m = row[x - left]!;
        if (m === 0) continue;
        const at = ((y % TILE) * TILE + (x % TILE)) * 4;
        const a = out[at + 3]!;
        if (a === 0) continue;
        const r0 = out[at]!;
        const g0 = out[at + 1]!;
        const b0 = out[at + 2]!;
        const [nr, ng, nb, na] = transform(r0, g0, b0, a);
        out[at] = Math.round(r0 + (nr - r0) * (m / 255));
        out[at + 1] = Math.round(g0 + (ng - g0) * (m / 255));
        out[at + 2] = Math.round(b0 + (nb - b0) * (m / 255));
        out[at + 3] = Math.round(a + (na - a) * (m / 255));
        changed = true;
      }
    }
    if (changed) {
      store.tiles.set(key, out);
      touched.push({ tx, ty });
    }
  }

  if (touched.length === 0) return false;
  store.commitPixels(label, transaction.end());
  store.invalidatePixels(touched);
  return true;
}

/** 反相（Ctrl+I）：sRGB 字节直接取补，跟真实 PS 的「反相」同一个定义。 */
export function invertColors(store: AppStore): boolean {
  return applyDestructiveAdjustment(store, '反相', (r, g, b, a) => [255 - r, 255 - g, 255 - b, a]);
}

/** 去色（Ctrl+Shift+U）：按亮度权重把 RGB 都设成同一个灰值，不改 alpha。 */
export function desaturateColors(store: AppStore): boolean {
  return applyDestructiveAdjustment(store, '去色', (r, g, b, a) => {
    const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
    return [gray, gray, gray, a];
  });
}

/** 色阶（Ctrl+L）：R/G/B 三通道各自套同一条 LUT（"主"通道，暂不支持分通道单独调）。 */
export function applyLevelsDestructive(store: AppStore, params: LevelsParams): boolean {
  const lut = levelsLut(params);
  return applyDestructiveAdjustment(store, '色阶', (r, g, b, a) => [lut[r]!, lut[g]!, lut[b]!, a]);
}

/** 曲线（Ctrl+M）：跟色阶同一个套法，只是 LUT 来自单调三次曲线而不是线性映射。 */
export function applyCurvesDestructive(store: AppStore, points: readonly CurvePoint[]): boolean {
  const lut = curvesLut(points);
  return applyDestructiveAdjustment(store, '曲线', (r, g, b, a) => [lut[r]!, lut[g]!, lut[b]!, a]);
}

/** 曝光度：复用 Camera Raw 式基础调整的曝光那一路 LUT，其余六个控件保持默认（不生效）。 */
export function applyExposureDestructive(store: AppStore, exposure: number): boolean {
  const lut = basicToneLut({ ...DEFAULT_BASIC_PARAMS, exposure });
  return applyDestructiveAdjustment(store, '曝光度', (r, g, b, a) => [lut[r]!, lut[g]!, lut[b]!, a]);
}

/** 色相/饱和度（Ctrl+U）：逐像素套 HSL 调整公式，跟调整图层同一份数学。 */
export function applyHueSaturationDestructive(store: AppStore, params: HslParams): boolean {
  return applyDestructiveAdjustment(store, '色相/饱和度', (r, g, b, a) => {
    const [nr, ng, nb] = adjustHsl(r, g, b, params);
    return [nr, ng, nb, a];
  });
}
